import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionAccessService from '../../services/subscriptionAccessService';
import CompanyShatterLoader from '../common/CompanyShatterLoader';

const roleKey = (role) =>
  String(
    role?.key ||
    role?.code ||
    role?.name ||
    ''
  )
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const FullPageLoader = ({ message = 'Checking authentication…' }) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: 24,
      background:
        'linear-gradient(155deg, color-mix(in srgb, var(--erp-primary, #4f46e5) 7%, #f8fafc), #f8fafc 48%, color-mix(in srgb, var(--erp-secondary, #0891b2) 7%, #f8fafc))',
    }}
  >
    <CompanyShatterLoader label={message} />
  </div>
);

const AccessState = ({ access, onLogout }) => {
  const expired = access?.reason === 'subscription_expired';
  const blocked =
    access?.reason === 'company_blocked' ||
    access?.reason === 'subscription_suspended';

  const color = expired ? '#d97706' : '#dc2626';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 22,
        background:
          'linear-gradient(155deg,#f8fafc,#eef2ff 48%,#fff7ed)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 620,
          padding: 30,
          borderRadius: 25,
          textAlign: 'center',
          background: 'rgba(255,255,255,.94)',
          border: '1px solid rgba(255,255,255,.9)',
          boxShadow: '0 28px 70px rgba(15,23,42,.12)',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto',
            borderRadius: 19,
            display: 'grid',
            placeItems: 'center',
            background: `linear-gradient(135deg,${color},var(--erp-accent,#f97316))`,
            color: '#fff',
            fontSize: 29,
          }}
        >
          {expired ? '⏳' : blocked ? '🔒' : '⚠️'}
        </div>

        <h2
          style={{
            margin: '15px 0 7px',
            color: '#0f172a',
            fontSize: 22,
          }}
        >
          {expired
            ? 'Subscription Expired'
            : blocked
              ? 'Company Access Blocked'
              : 'Company Access Unavailable'}
        </h2>

        <p
          style={{
            margin: '0 auto',
            maxWidth: 500,
            color: '#64748b',
            fontSize: 11.5,
            lineHeight: 1.6,
          }}
        >
          {access?.message ||
            'This company cannot access operational modules at this time.'}
        </p>

        {access?.endDate ? (
          <div
            style={{
              marginTop: 12,
              color,
              fontSize: 10,
              fontWeight: 900,
            }}
          >
            Subscription end date:{' '}
            {access.endDate.toLocaleDateString('en-IN')}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 18,
            padding: 11,
            borderRadius: 12,
            background: `${color}0d`,
            border: `1px solid ${color}20`,
            color: '#475569',
            fontSize: 8.8,
            lineHeight: 1.5,
          }}
        >
          Your company data remains stored. Contact the system administrator
          to restore login or renew the subscription.
        </div>

        <button
          type="button"
          onClick={onLogout}
          style={{
            marginTop: 17,
            minHeight: 40,
            padding: '9px 16px',
            border: 0,
            borderRadius: 12,
            background: 'linear-gradient(135deg,var(--erp-primary,#4f46e5),var(--erp-secondary,#0891b2))',
            color: '#fff',
            fontWeight: 900,
            cursor: 'pointer',
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute = ({
  children,
  requireSuperAdmin = true,
}) => {
  const location = useLocation();

  const {
    user,
    userData,
    roleData,
    loading,
    authError,
    isSuperAdmin,
    isAuthenticated,
    logout,
  } = useAuth();

  const normalizedRole = roleKey(roleData || userData?.role);

  const actualSuperAdmin = Boolean(
    isSuperAdmin ||
      userData?.metadata?.isSuperAdmin === true ||
      normalizedRole === 'super_admin'
  );

  const tenantId =
    userData?.tenantId?.id ||
    userData?.tenantIdValue ||
    (typeof userData?.tenantId === 'string' ? userData.tenantId : '') ||
    userData?.tenant?.id ||
    '';

  const cachedCompanyAccess = React.useMemo(
    () => (!requireSuperAdmin && tenantId ? subscriptionAccessService.peekTenantAccess(tenantId) : null),
    [requireSuperAdmin, tenantId]
  );
  const [accessLoading, setAccessLoading] = React.useState(false);
  const [companyAccess, setCompanyAccess] = React.useState(() => cachedCompanyAccess);
  const [accessTick, setAccessTick] = React.useState(0);

  const hasCompletedInitialAccessCheckRef = React.useRef(Boolean(cachedCompanyAccess));

  // Once the protected CMS has rendered successfully, background auth/access
  // checks must never replace it with a full-page loader.
  const hasRenderedProtectedAppRef = React.useRef(false);

  // Reset the initial access gate only when the authenticated principal/tenant changes.
  // Normal page navigation and background access checks must never blank/reload the page.
  React.useEffect(() => {
    const cached = (!requireSuperAdmin && tenantId)
      ? subscriptionAccessService.peekTenantAccess(tenantId)
      : null;
    hasCompletedInitialAccessCheckRef.current = Boolean(cached);
    hasRenderedProtectedAppRef.current = false;
    setCompanyAccess(cached);
  }, [
    user?.uid,
    tenantId,
    requireSuperAdmin,
    actualSuperAdmin,
  ]);

  // Re-check tenant/subscription access while a company session stays open.
  // The timer remains for security, but every check after the first one is silent.
  React.useEffect(() => {
    if (requireSuperAdmin || actualSuperAdmin || !user || !tenantId) {
      return undefined;
    }

    const timer = window.setInterval(
      () => setAccessTick((value) => value + 1),
      60000 // silent security re-check; does not refresh/reload the page
    );

    return () => window.clearInterval(timer);
  }, [
    requireSuperAdmin,
    actualSuperAdmin,
    user,
    tenantId,
  ]);

  React.useEffect(() => {
    let active = true;

    const check = async () => {
      if (
        requireSuperAdmin ||
        actualSuperAdmin ||
        !user ||
        !tenantId
      ) {
        if (active) {
          setCompanyAccess(null);
          setAccessLoading(false);
        }
        return;
      }

      const isInitialCheck =
        !hasCompletedInitialAccessCheckRef.current;

      try {
        // Show the full-page access loader only once when the company session
        // is first resolved. Periodic checks and route changes stay invisible.
        if (isInitialCheck && active) {
          setAccessLoading(true);
        }

        const result =
          await subscriptionAccessService.getTenantAccess(tenantId, { force: accessTick > 0 });

        if (active) {
          setCompanyAccess(result);
          hasCompletedInitialAccessCheckRef.current = true;
        }
      } catch (error) {
        console.error('Tenant subscription access check failed:', error);

        if (active) {
          setCompanyAccess({
            allowed: false,
            reason: 'access_check_failed',
            message:
              'Company access could not be verified. Please contact the administrator.',
          });
        }
      } finally {
        if (active && isInitialCheck) {
          setAccessLoading(false);
          hasCompletedInitialAccessCheckRef.current = true;
        }
      }
    };

    check();

    return () => {
      active = false;
    };
  }, [
    requireSuperAdmin,
    actualSuperAdmin,
    user,
    tenantId,
    accessTick,
  ]);

  const shouldShowInitialLoader =
    !hasRenderedProtectedAppRef.current &&
    (loading || accessLoading);

  if (shouldShowInitialLoader) {
    return (
      <FullPageLoader
        message={
          accessLoading
            ? 'Checking company subscription…'
            : 'Checking authentication…'
        }
      />
    );
  }

  const authenticated = Boolean(isAuthenticated || user);

  if (!authenticated) {
    return (
      <Navigate
        to={requireSuperAdmin ? '/login' : '/company-login'}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (requireSuperAdmin) {
    if (!actualSuperAdmin) {
      return <Navigate to="/company-dashboard" replace />;
    }

    hasRenderedProtectedAppRef.current = true;
    return children;
  }

  if (actualSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const hasProfile = Boolean(
    userData &&
      (
        tenantId ||
        roleData ||
        userData?.roleId ||
        userData?.principalType === 'employee'
      )
  );

  if (!hasProfile) {
    return (
      <div
        style={{
          minHeight: '70vh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          background: 'linear-gradient(145deg,#f8fafc,color-mix(in srgb,var(--erp-primary,#4f46e5) 6%,#f8fafc))',
        }}
      >
        <div
          style={{
            maxWidth: 540,
            padding: 28,
            borderRadius: 22,
            textAlign: 'center',
            background: '#fff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 20px 52px rgba(15,23,42,.08)',
          }}
        >
          <div style={{ fontSize: 42 }}>⚠️</div>
          <h2 style={{ margin: '12px 0 6px', color: '#0f172a' }}>
            Account Profile Not Ready
          </h2>
          <p
            style={{
              margin: 0,
              color: '#64748b',
              fontSize: 11.5,
              lineHeight: 1.55,
            }}
          >
            The account is authenticated, but its company/employee profile
            or assigned role could not be resolved.
          </p>

          {authError ? (
            <div
              style={{
                marginTop: 11,
                padding: 9,
                borderRadius: 10,
                background: 'rgba(217,119,6,.08)',
                color: '#b45309',
                fontSize: 9.5,
                fontWeight: 750,
              }}
            >
              {authError}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (companyAccess && companyAccess.allowed === false) {
    return (
      <AccessState
        access={companyAccess}
        onLogout={() => {
          if (logout) {
            logout();
          } else {
            window.location.assign('/company-login');
          }
        }}
      />
    );
  }

  hasRenderedProtectedAppRef.current = true;
  return children;
};

export default ProtectedRoute;